import { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import {
  parsePlanAiFeatures,
  parsePlanFeatures,
  type PlanAiFeatureMap,
  type PlanFeatureMap,
} from '../constants/planFeatures';
import type { AccountPassCategory } from '../utils/subscriptionPassStorage';
import { fetchPlanStorageLimits, PLUS_CANONICAL_PLAN_KEY, PLUS_PLAN_KEYS } from '../services/planPermissionsService';

/**
 * 이용권별 기본 기능 권한·학급 상한(platform_plan_permissions, 누구나 읽기).
 * - 표가 없거나(마이그레이션 전) 읽기 실패면 rows = null → 호출부가 기존 고정 규칙을 쓴다.
 * - 관리자 화면 저장 후 PLAN_PERMISSIONS_CHANGED_EVENT 로 다시 읽는다.
 * - PLUS 계열(free 가 아닌 모든 분류)은 하나로 합쳐(mergePlusGateRows) 모든 PLUS 키에 같은 행을 둔다.
 */
export type PlanGateRow = {
  maxClasses: number | null;
  features: Partial<PlanFeatureMap>;
  /** 월 AI 크레딧(없거나 읽기 실패면 null) */
  aiMonthlyCredits?: number | null;
  /** 이미지 저장 용량 한도(바이트). 열이 없거나(마이그레이션 전) 읽기 실패면 null */
  storageLimitBytes?: number | null;
  /** AI 열(ai_learning_plan·ai_worksheet·ai_slides·ai_other). 읽기 실패·열 없음이면 undefined */
  ai?: { learningPlan: boolean; worksheet: boolean; slides: boolean; other: boolean };
  /** AI 세부 기능(extra.features.ai_*). 값이 없으면 ai.other 를 따른다 */
  aiFeatures?: PlanAiFeatureMap;
};
export type PlanGateRows = Partial<Record<AccountPassCategory, PlanGateRow>>;

export const PLAN_PERMISSIONS_CHANGED_EVENT = 'cp-plan-permissions-changed';

type Store = { rows: PlanGateRows | null; loaded: boolean; inflight: Promise<void> | null };
const store: Store = { rows: null, loaded: false, inflight: null };
const listeners = new Set<() => void>();
let subscribed = false;

async function fetchRows(): Promise<PlanGateRows | null> {
  const { data, error } = await supabase.from('platform_plan_permissions').select('plan_key, max_classes, ai_monthly_credits, ai_learning_plan, ai_worksheet, ai_slides, ai_other, extra');
  if (error || !Array.isArray(data)) return null;
  const out: PlanGateRows = {};
  for (const raw of data as {
    plan_key?: unknown;
    max_classes?: unknown;
    ai_monthly_credits?: unknown;
    ai_learning_plan?: unknown;
    ai_worksheet?: unknown;
    ai_slides?: unknown;
    ai_other?: unknown;
    extra?: unknown;
  }[]) {
    if (typeof raw.plan_key !== 'string') continue;
    const mc = Number(raw.max_classes);
    const credits = raw.ai_monthly_credits == null ? NaN : Number(raw.ai_monthly_credits);
    out[raw.plan_key as AccountPassCategory] = {
      maxClasses: Number.isFinite(mc) && mc >= 1 ? Math.round(mc) : null,
      features: parsePlanFeatures(raw.extra),
      aiMonthlyCredits: Number.isFinite(credits) && credits >= 0 ? Math.round(credits) : null,
      ai: {
        learningPlan: raw.ai_learning_plan === true,
        worksheet: raw.ai_worksheet === true,
        slides: raw.ai_slides === true,
        other: raw.ai_other === true,
      },
      aiFeatures: parsePlanAiFeatures(raw.extra),
    };
  }
  // 저장 용량 열은 별도 조회(열이 없어도 위의 권한 조회는 그대로 쓰도록)
  const storage = await fetchPlanStorageLimits();
  if (storage) {
    for (const [k, v] of Object.entries(storage)) {
      const row = out[k as AccountPassCategory];
      if (row) row.storageLimitBytes = v;
    }
  }
  return mergePlusGateRows(out);
}

/** 하나라도 켜져 있으면 켬(값이 하나도 없으면 undefined) */
function anyOn(values: (boolean | undefined)[]): boolean | undefined {
  const defined = values.filter((v): v is boolean => typeof v === 'boolean');
  return defined.length ? defined.some(Boolean) : undefined;
}
function maxOf(values: (number | null | undefined)[]): number | null | undefined {
  const nums = values.filter((v): v is number => typeof v === 'number');
  if (nums.length) return Math.max(...nums);
  return values.some((v) => v === null) ? null : undefined;
}

/**
 * PLUS 계열 행을 하나로 합쳐 모든 PLUS 키에 같은 행을 둔다(관리자 화면 「PLUS 이용권」 열과 같은 값).
 * 행 값은 관리자 저장·마이그레이션으로 항상 같지만, 혹시 다르면 가장 넓은 권한(켜짐 우선·최댓값)을 쓴다.
 */
export function mergePlusGateRows(rows: PlanGateRows): PlanGateRows {
  const plus = PLUS_PLAN_KEYS.map((k) => rows[k]).filter((r): r is PlanGateRow => Boolean(r));
  if (!plus.length) return rows;
  const featureKeys = new Set(plus.flatMap((r) => Object.keys(r.features))) as Set<keyof PlanFeatureMap>;
  const features: Partial<PlanFeatureMap> = {};
  featureKeys.forEach((k) => {
    const v = anyOn(plus.map((r) => r.features[k]));
    if (typeof v === 'boolean') features[k] = v;
  });
  const withAi = plus.filter((r) => r.ai);
  const ai = withAi.length
    ? {
        learningPlan: withAi.some((r) => r.ai?.learningPlan),
        worksheet: withAi.some((r) => r.ai?.worksheet),
        slides: withAi.some((r) => r.ai?.slides),
        other: withAi.some((r) => r.ai?.other),
      }
    : undefined;
  const aiKeys = new Set(plus.flatMap((r) => Object.keys(r.aiFeatures ?? {}))) as Set<keyof PlanAiFeatureMap>;
  const aiFeatures: PlanAiFeatureMap = {};
  aiKeys.forEach((k) => {
    aiFeatures[k] = plus.some((r) => {
      const v = r.aiFeatures?.[k];
      return typeof v === 'boolean' ? v : Boolean(r.ai?.other);
    });
  });
  const base = rows[PLUS_CANONICAL_PLAN_KEY] ?? plus[0];
  const merged: PlanGateRow = {
    ...base,
    maxClasses: maxOf(plus.map((r) => r.maxClasses)) ?? null,
    features,
    aiMonthlyCredits: maxOf(plus.map((r) => r.aiMonthlyCredits)),
    storageLimitBytes: maxOf(plus.map((r) => r.storageLimitBytes)),
    ai,
    aiFeatures,
  };
  const out: PlanGateRows = { ...rows };
  for (const k of PLUS_PLAN_KEYS) out[k] = merged;
  return out;
}

function reload(): Promise<void> {
  if (store.inflight) return store.inflight;
  store.inflight = fetchRows()
    .catch(() => null)
    .then((rows) => {
      store.rows = rows;
    })
    .finally(() => {
      store.loaded = true;
      store.inflight = null;
      listeners.forEach((fn) => fn());
    });
  return store.inflight;
}

function ensureSubscriptions() {
  if (subscribed || typeof window === 'undefined') return;
  subscribed = true;
  supabase.auth.onAuthStateChange((event) => {
    if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') void reload();
  });
  window.addEventListener(PLAN_PERMISSIONS_CHANGED_EVENT, () => void reload());
}

export function notifyPlanPermissionsChanged(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(PLAN_PERMISSIONS_CHANGED_EVENT));
}

/** 이용권별 행(없으면 null). 첫 사용 시 한 번 읽고 앱 전체가 공유한다. */
export function usePlanGateRows(): PlanGateRows | null {
  const [, setTick] = useState(0);
  useEffect(() => {
    ensureSubscriptions();
    const fn = () => setTick((t) => t + 1);
    listeners.add(fn);
    if (!store.loaded && !store.inflight) void reload();
    return () => {
      listeners.delete(fn);
    };
  }, []);
  return store.rows;
}
